import { createContext, useContext, useState, ReactNode } from "react";

type Language = "en" | "lt";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Create a default value to prevent undefined context errors
const defaultContextValue: LanguageContextType = {
  language: "en",
  setLanguage: () => {},
  t: (key: string) => key,
};

const LanguageContext = createContext<LanguageContextType>(defaultContextValue);

const translations = {
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.pain": "The pain we solve",
    "nav.expertise": "Our expertise",
    "nav.experience": "Experience",
    "nav.why": "Why Shape?",
    "nav.login": "Login",
    "nav.signOut": "Sign Out",
    
    // Scroll Section Names
    "scroll.pain": "Challenges",
    "scroll.process": "Our Process",
    "scroll.why": "Why Us",
    "scroll.stats": "Results",
    "scroll.testimonials": "Client Stories",
    "scroll.cta": "Get Started",
    
    // Hero Section
    "hero.tagline": "TURNING VISION INTO DIGITAL",
    "hero.title": "SHAPE",
    "hero.freelancers": "Freelancers deliver",
    "hero.fragments": "fragments",
    "hero.weDeliver": "We deliver the",
    "hero.completeStrategy": "complete strategy",
    "hero.description": "Shape is a",
    "hero.integratedPartner": "360° growth & marketing partner",
    "hero.cohesive": "for digital-first brands.",
    "hero.exploreCourses": "Explore Courses",
    "hero.marketingAgency": "Marketing Agency",
    
    // Stats Section
    "stats.title": "Agency",
    "stats.numbers": "Numbers",
    "stats.revenue": "Revenue from Ads",
    "stats.experience": "Years of Experience",
    "stats.creatives": "Creatives Created",
    
    // Pain Section
    "pain.titleStart": "The pain we",
    "pain.titleHighlight": "solve",
    "pain.fragmented": "Fragmented marketing is holding you back",
    "pain.description": "Shape plans, builds and runs your entire marketing – Meta & Google ads, web, video, email and social – so you don't have to manage 5 different freelancers...",
    "pain.shapeBrings": "brings it all together",
    "pain.team": "team",
    "pain.direction": "direction",
    "pain.result": "measurable result",
    
    // Expertise Section
    "expertise.title": "Our",
    "expertise.capabilities": "capabilities",
    "expertise.subtitle": "Full-spectrum marketing solutions delivered by integrated specialists",
    "expertise.visual": "Visual Identity & Content",
    "expertise.visualDesc": "Static & animated visuals, engaging reels, UGC content.",
    "expertise.performance": "Performance Channels",
    "expertise.performanceDesc": "Meta Ads, Google Ads, conversion-driven funnels.",
    "expertise.retention": "Retention & Loyalty",
    "expertise.retentionDesc": "Newsletters, retargeting campaigns, automation.",
    "expertise.social": "Social media management",
    "expertise.socialDesc": "Strategy & content calendar, posting, customer support, analytics.",
    "expertise.website": "Website Solutions",
    "expertise.websiteDesc": "Custom websites, landing pages, e-commerce platforms.",
    "expertise.ai": "AI Automation",
    "expertise.aiDesc": "AI call agents, AI video production, process automation.",
    
    // Process Section
    "process.title": "Our",
    "process.process": "process",
    "process.subtitle": "From one idea to a full 360° growth system",
    "process.discovery": "Discovery",
    "process.discoveryDesc": "We audit your brand, funnel and data across ads, web, email and social to uncover the real growth opportunities.",
    "process.strategy": "Strategy",
    "process.strategyDesc": "We build one clear plan so every channel works toward the same goal.",
    "process.execution": "Execution",
    "process.executionDesc": "We run Meta, Google, web, email, social and video as one connected system.",
    "process.growth": "Growth",
    "process.growthDesc": "Continuous optimization and scaling based on data-driven insights.",
    
    // Experience Section
    "experience.title": "Experience",
    "experience.subtitle": "Shaping growth across industries.",
    "experience.wellness": "Shaping",
    "experience.wellnessBrand": "wellness",
    "experience.wellnessEnd": "brands",
    "experience.ecommerce": "Shaping",
    "experience.ecommerceGrowth": "e-commerce",
    "experience.ecommerceEnd": "growth",
    "experience.hospitality": "Shaping",
    "experience.hospitalityExp": "hospitality",
    "experience.hospitalityEnd": "experiences",
    
    // Why Section
    "why.title": "Why",
    "why.shape": "Shape",
    "why.subtitle": "Why Shape, not freelancers or classic agencies?",
    "why.oneTeam": "One team. Zero fragmentation.",
    "why.oneTeamDesc": "Never juggle 5 freelancers again — you get one fully synced 360° team.",
    "why.endToEnd": "Creative + performance under one roof.",
    "why.endToEndDesc": "Our media buyers, designers and video team work as one — faster, sharper, higher-performing output.",
    "why.faster": "Clear structure. Smooth execution.",
    "why.fasterDesc": "One organized process, consistent communication, and no more chaos between channels.",
    "why.vision": "Your vision, turned into digital",
    "why.visionDesc": "SHAPE.",
    "why.experience": "Experience the difference of a unified marketing approach",
    
    // Testimonials Section
    "testimonials.title": "Client",
    "testimonials.stories": "stories",
    "testimonials.sarah": "Shape transformed our digital presence completely. Their integrated approach delivered results beyond our expectations.",
    "testimonials.michael": "Working with Shape was seamless. One team, one vision, measurable results. Exactly what we needed.",
    "testimonials.emma": "The holistic strategy they created helped us scale our brand across all channels. Truly impressive work.",
    "testimonials.sarahRole": "Marketing Director",
    "testimonials.michaelRole": "CEO",
    "testimonials.emmaRole": "Brand Manager",
    
    // CTA Section
    "cta.ready": "READY",
    "cta.toTurn": "to turn your vision into digital",
    "cta.shape": "SHAPE?",
    "cta.growthNow": "Shape your growth now",
    "cta.bookCall": "Book a call",
    
    // Footer
    "footer.tagline": "Turning vision into digital reality.",
    "footer.quickLinks": "Quick Links",
    "footer.capabilities": "Capabilities",
    "footer.experience": "Experience",
    "footer.solutions": "Solutions",
    "footer.whyUs": "Why Us",
    "footer.contact": "Contact",
    "footer.stayUpdated": "Stay Updated",
    "footer.getLatest": "Get the latest insights and updates.",
    "footer.yourEmail": "Your email",
    "footer.copyright": "© 2025 Shape. All rights reserved.",
    "footer.trustedBy": "Trusted by innovative brands worldwide",
    "footer.subscribeSuccess": "Success!",
    "footer.subscribeDesc": "You've been subscribed to our newsletter.",
    
    // Testimonial Names
    "testimonials.sarahName": "Sarah Johnson",
    "testimonials.michaelName": "Michael Chen",
    "testimonials.emmaName": "Emma Williams",
    "testimonials.sarahCompany": "TechCorp",
    "testimonials.michaelCompany": "GrowthLab",
    "testimonials.emmaCompany": "Wellness Co",
    
    // Auth
    "auth.welcome": "Welcome Back",
    "auth.join": "Join SHAPE",
    "auth.signInDesc": "Sign in to continue your learning journey",
    "auth.signUpDesc": "Create an account to start learning",
    "auth.fullName": "Full Name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.signIn": "Sign In",
    "auth.signUp": "Sign Up",
    "auth.noAccount": "Don't have an account? Sign up",
    "auth.haveAccount": "Already have an account? Sign in",
    "auth.backToMain": "Back to main",
    
    // Common
    "common.loading": "Loading...",
    
    // Portfolio
    "portfolio.title": "Portfolio",
    "portfolio.subtitle": "Highlights of our best campaigns and projects across different channels",
    "portfolio.all": "All Projects",
    "portfolio.meta": "Meta Ads",
    "portfolio.google": "Google Ads",
    "portfolio.video": "Video Creation",
    "portfolio.youtube": "YouTube",
    "portfolio.loading": "Loading projects...",
    "portfolio.noProjects": "No projects found in this category.",
    "portfolio.photos": "photos",
    "portfolio.videos": "videos",
    "portfolio.allClients": "All Clients",
    "portfolio.client": "Client",
    "portfolio.openInstagram": "Open on Instagram",
    "portfolio.instagramVideo": "Instagram video",
    
    // Navigation Menu
    "nav.process": "Process",
    "nav.workExamples": "Work Examples",
    "nav.portfolio": "Portfolio",
    "nav.contact": "Contact",
    "nav.accountDashboard": "Account Dashboard",
    "nav.adminPanel": "Admin Panel",
    "nav.myCourses": "My Courses",
    
    // Account Dashboard
    "account.title": "Account Dashboard",
    "account.subtitle": "Manage your profile and settings",
    "account.profileTab": "Profile Management",
    "account.learningTab": "My Learning",
    "account.settingsTab": "Account Settings",
    "account.profileTitle": "Profile Information",
    "account.profileDesc": "Update your profile details and avatar",
    "account.uploadAvatar": "Upload Avatar",
    "account.uploading": "Uploading...",
    "account.avatarHint": "JPG, PNG or GIF. Max size 2MB.",
    "account.email": "Email",
    "account.emailHint": "Email cannot be changed here",
    "account.fullName": "Full Name",
    "account.fullNamePlaceholder": "Enter your full name",
    "account.bio": "Bio",
    "account.bioPlaceholder": "Tell us about yourself",
    "account.saveChanges": "Save Changes",
    "account.myCourses": "My Courses",
    "account.trackProgress": "Track your learning progress",
    "account.noEnrollments": "You haven't enrolled in any courses yet",
    "account.browseCourses": "Browse Courses",
    "account.progress": "Progress",
    "account.continueLearning": "Continue Learning",
    "account.myCertificates": "My Certificates",
    "account.achievements": "Your earned achievements",
    "account.noCertificates": "No certificates earned yet",
    "account.earnCertificates": "Complete courses to earn certificates",
    "account.issuedOn": "Issued on",
    "account.view": "View",
    "account.changePassword": "Change Password",
    "account.updatePassword": "Update your password",
    "account.newPassword": "New Password",
    "account.confirmPassword": "Confirm Password",
    "account.passwordMinLength": "Password must be at least 6 characters",
    "account.updatePasswordBtn": "Update Password",
    "account.deleteAccount": "Delete Account",
    "account.deleteAccountDesc": "Permanently delete your account",
    "account.deleteWarning": "This action cannot be undone. All your data will be permanently deleted.",
    "account.deleteMyAccount": "Delete My Account",
    
    // Admin Dashboard
    "admin.title": "Admin Dashboard",
    "admin.welcome": "Welcome back! Here's your overview",
    "admin.totalProjects": "Total Projects",
    "admin.portfolioItems": "Portfolio items",
    "admin.totalCourses": "Total Courses",
    "admin.publishedDrafts": "Published & drafts",
    "admin.totalStudents": "Total Students",
    "admin.registeredUsers": "Registered users",
    "admin.enrollments": "Enrollments",
    "admin.activeCompleted": "Active & completed",
    "admin.quickActions": "Quick Actions",
    "admin.portfolioMgmt": "Portfolio Management",
    "admin.portfolioMgmtDesc": "Manage portfolio projects and media",
    "admin.courseMgmt": "Course Management",
    "admin.courseMgmtDesc": "Manage courses, modules, and lessons",
    "admin.userMgmt": "User Management",
    "admin.userMgmtDesc": "Manage users and permissions",
    "admin.settings": "Settings",
    "admin.settingsDesc": "Configure system settings",
    "admin.manage": "Manage",
    "admin.backToHome": "Back to Home",
    "admin.backToAdmin": "Back to Admin",
    "admin.addCourse": "Add New Course",
    "admin.editCourse": "Edit Course",
    "admin.courseTitle": "Title",
    "admin.courseSlug": "Slug",
    "admin.courseDescription": "Description",
    "admin.coursePrice": "Price",
    "admin.courseCurrency": "Currency",
    "admin.courseLevel": "Level",
    "admin.courseStatus": "Status",
    "admin.courseDuration": "Duration (hours)",
    "admin.courseThumbnail": "Course Thumbnail",
    "admin.uploadThumbnail": "Click to upload thumbnail",
    "admin.thumbnailUploaded": "Thumbnail uploaded successfully",
    "admin.createCourse": "Create Course",
    "admin.updateCourse": "Update Course",
    "admin.existingCourses": "Existing Courses",
    "admin.draft": "Draft",
    "admin.published": "Published",
    "admin.archived": "Archived",
    "admin.addModule": "Add Module",
    "admin.editModule": "Edit Module",
    "admin.moduleTitle": "Module Title",
    "admin.moduleDescription": "Module Description",
    "admin.createModule": "Create Module",
    "admin.updateModule": "Update Module",
    "admin.addLesson": "Add Lesson",
    "admin.editLesson": "Edit Lesson",
    "admin.lessonTitle": "Lesson Title",
    "admin.lessonDescription": "Lesson Description",
    "admin.lessonType": "Type",
    "admin.lessonDuration": "Duration (min)",
    "admin.videoLesson": "Video",
    "admin.textLesson": "Text",
    "admin.quizLesson": "Quiz",
    "admin.videoUrl": "Video URL",
    "admin.lessonContent": "Content",
    "admin.freePreview": "Free Preview",
    "admin.createLesson": "Create Lesson",
    "admin.updateLesson": "Update Lesson",
    "admin.edit": "Edit",
    "admin.delete": "Delete",
    "admin.cancel": "Cancel",
    
    // Course Catalog
    "courses.title": "Our Courses",
    "courses.subtitle": "Learn from industry experts and advance your career",
    "courses.search": "Search courses...",
    "courses.allLevels": "All Levels",
    "courses.beginner": "Beginner",
    "courses.intermediate": "Intermediate",
    "courses.advanced": "Advanced",
    "courses.allPrices": "All",
    "courses.free": "Free",
    "courses.paid": "Paid",
    "courses.sortBy": "Sort by",
    "courses.sortNewest": "Newest",
    "courses.sortPopular": "Most Popular",
    "courses.sortPriceLow": "Price: Low to High",
    "courses.sortPriceHigh": "Price: High to Low",
    "courses.featured": "Featured",
    "courses.students": "students",
    "courses.hours": "hours",
    "courses.viewCourse": "View Course",
    "courses.noCourses": "No courses found matching your criteria",
    "courses.loading": "Loading courses...",
    "courses.instructor": "Instructor",
    "courses.workingOnIt": "We're still working on this page",
    
    // Course Detail
    "courseDetail.overview": "Overview",
    "courseDetail.curriculum": "Curriculum",
    "courseDetail.reviews": "Reviews",
    "courseDetail.instructor": "Instructor",
    "courseDetail.enrollNow": "Enroll Now",
    "courseDetail.enrolled": "Enrolled",
    "courseDetail.studentsEnrolled": "students enrolled",
    "courseDetail.whatYouLearn": "What you'll learn",
    "courseDetail.requirements": "Requirements",
    "courseDetail.description": "Description",
    "courseDetail.modules": "modules",
    "courseDetail.lessons": "lessons",
    "courseDetail.totalDuration": "Total duration",
    "courseDetail.level": "Level",
    "courseDetail.lastUpdated": "Last updated",
    "courseDetail.language": "Language",
    "courseDetail.certificate": "Certificate of completion",
    "courseDetail.preview": "Preview",
    "courseDetail.locked": "Locked",
    "courseDetail.minutes": "min",
    "courseDetail.noReviews": "No reviews yet. Be the first to review!",
    "courseDetail.writeReview": "Write a Review",
    "courseDetail.yourRating": "Your Rating",
    "courseDetail.yourReview": "Your Review",
    "courseDetail.submit": "Submit Review",
    "courseDetail.instructorBio": "About the Instructor",
    "courseDetail.otherCourses": "Other courses by this instructor",
    "courseDetail.enrollmentSuccess": "Successfully enrolled in course!",
    "courseDetail.enrollmentError": "Failed to enroll. Please try again.",
    "courseDetail.reviewSuccess": "Review submitted successfully!",
    "courseDetail.reviewError": "Failed to submit review. Please try again.",
    "courseDetail.loginToEnroll": "Login to enroll",
    "courseDetail.loginToReview": "Login to write a review",
    "courseDetail.alreadyEnrolled": "You're enrolled in this course",
    "courseDetail.continueLearning": "Continue Learning",
    "courseDetail.reviewPlaceholder": "Share your thoughts about this course...",
    "courseDetail.submitReview": "Submit Review",
    "courseDetail.startLearning": "Start Learning",
    
    // Course Player
    "coursePlayer.courseContent": "Course Content",
    "coursePlayer.completed": "Completed",
    "coursePlayer.markComplete": "Mark as Complete",
    "coursePlayer.nextLesson": "Next Lesson",
    "coursePlayer.previousLesson": "Previous Lesson",
    "coursePlayer.backToCourse": "Back to Course",
    "coursePlayer.progress": "Progress",
    "coursePlayer.lesson": "Lesson",
    "coursePlayer.of": "of",
    "coursePlayer.noContent": "No content available for this lesson.",
    "coursePlayer.loadingLesson": "Loading lesson...",
    "coursePlayer.completedLesson": "Lesson completed!",
    "coursePlayer.errorLoading": "Error loading lesson. Please try again.",
    "coursePlayer.notEnrolled": "You must be enrolled to access this course.",
    "coursePlayer.enrollNow": "Enroll Now",
    
    // Course Editor
    "courseEditor.editMode": "Edit Mode",
    "courseEditor.editLesson": "Edit Lesson",
    "courseEditor.lessonTitle": "Lesson Title",
    "courseEditor.lessonTitlePlaceholder": "Enter lesson title...",
    "courseEditor.lessonType": "Lesson Type",
    "courseEditor.typeVideo": "Video",
    "courseEditor.typeText": "Text",
    "courseEditor.typeQuiz": "Quiz",
    "courseEditor.videoUrl": "Video URL",
    "courseEditor.videoUrlHint": "Use YouTube embed URL (e.g., https://www.youtube.com/embed/VIDEO_ID)",
    "courseEditor.description": "Description",
    "courseEditor.descriptionPlaceholder": "Brief description of the lesson...",
    "courseEditor.content": "Content (HTML)",
    "courseEditor.contentPlaceholder": "Enter lesson content with HTML formatting...",
    "courseEditor.contentHint": "You can use HTML tags for formatting (e.g., <h2>, <p>, <ul>, <img>)",
    "courseEditor.duration": "Duration (minutes)",
    "courseEditor.freePreview": "Free Preview",
    "courseEditor.yes": "Yes",
    "courseEditor.no": "No",
    "courseEditor.save": "Save",
    "courseEditor.cancel": "Cancel",
    "courseEditor.lessonSaved": "Lesson saved successfully!",
    "courseEditor.saveFailed": "Failed to save lesson. Please try again.",
    "courseEditor.editingAs": "Editing as",
    "courseEditor.clickToEdit": "Click to edit",
    
    // Rich Text Editor
    "richEditor.heading": "Heading",
    "richEditor.paragraph": "Paragraph",
    "richEditor.bold": "Bold",
    "richEditor.italic": "Italic",
    "richEditor.underline": "Underline",
    "richEditor.strikethrough": "Strikethrough",
    "richEditor.bulletList": "Bullet List",
    "richEditor.numberedList": "Numbered List",
    "richEditor.alignLeft": "Align Left",
    "richEditor.alignCenter": "Align Center",
    "richEditor.alignRight": "Align Right",
    "richEditor.insertLink": "Insert Link",
    "richEditor.linkUrl": "Link URL",
    "richEditor.insertImage": "Insert Image",
    "richEditor.imageUrl": "Image URL",
    "richEditor.uploadImage": "Upload Image",
    "richEditor.insertVideo": "Insert Video",
    "richEditor.videoUrl": "Video URL",
    "richEditor.videoHint": "Supports YouTube and Vimeo URLs",
    "richEditor.codeBlock": "Code Block",
    "richEditor.blockquote": "Quote",
    "richEditor.horizontalRule": "Horizontal Line",
    "richEditor.undo": "Undo",
    "richEditor.redo": "Redo",
    "richEditor.insert": "Insert",
    "richEditor.or": "or",
    "richEditor.preview": "Preview",
    "richEditor.edit": "Edit",
    "richEditor.invalidImageType": "Please select an image file",
    "richEditor.imageTooLarge": "Image must be less than 5MB",
    "richEditor.imageUploaded": "Image uploaded successfully",
    "richEditor.uploadFailed": "Failed to upload image",
  },
  lt: {
    // Navigation
    "nav.home": "Pradžia",
    "nav.pain": "Kokią problemą sprendžiame",
    "nav.expertise": "Mūsų ekspertizė",
    "nav.experience": "Patirtis",
    "nav.why": "Kodėl Shape?",
    "nav.login": "Prisijungti",
    "nav.signOut": "Atsijungti",
    
    // Scroll Section Names
    "scroll.pain": "Iššūkiai",
    "scroll.process": "Procesas",
    "scroll.why": "Kodėl mes",
    "scroll.stats": "Rezultatai",
    "scroll.testimonials": "Atsiliepimai",
    "scroll.cta": "Pradėkime",
    
    // Hero Section
    "hero.tagline": "VIZIJĄ PAVERČIAME SKAITMENINIU",
    "hero.title": "SHAPE",
    "hero.freelancers": "Augimas nėra atsitiktinių sprendimų",
    "hero.fragments": "rezultatas",
    "hero.weDeliver": "Tai – kryptingos strategijos",
    "hero.completeStrategy": "pasekmė",
    "hero.description": '"Shape" –',
    "hero.integratedPartner": "360° augimo ir marketingo partneris",
    "hero.cohesive": "skaitmeniniams prekių ženklams.",
    "hero.exploreCourses": "Naršyti Kursus",
    "hero.marketingAgency": "Rinkodaros Agentūra",
    
    // Stats Section
    "stats.title": "Agentūros",
    "stats.numbers": "skaičiai",
    "stats.revenue": "Pajamos iš reklamų",
    "stats.experience": "Metų patirtis",
    "stats.creatives": "Sukurtų kūrinių",
    
    // Pain Section
    "pain.titleStart": "Kokią problemą",
    "pain.titleHighlight": "sprendžiame",
    "pain.fragmented": "Fragmentuota rinkodara jus stabdo",
    "pain.description": "Shape planuoja, kuria ir vykdo visą jūsų rinkodarą – Meta ir Google reklamas, svetaines, video, el. paštą ir socialinius tinklus – kad jums nereikėtų valdyti 5 skirtingų laisvų samdančių...",
    "pain.shapeBrings": "suvienija viską",
    "pain.team": "komanda",
    "pain.direction": "kryptis",
    "pain.result": "išmatuojamas rezultatas",
    
    // Expertise Section
    "expertise.title": "Mūsų",
    "expertise.capabilities": "galimybės",
    "expertise.subtitle": "Visos apimties rinkodaros sprendimai, teikiami integruotų specialistų",
    "expertise.visual": "Vizualinė Tapatybė ir Turinys",
    "expertise.visualDesc": "Statiniai ir animuoti vaizdai, patrauklūs reels, UGC turinys.",
    "expertise.performance": "Našumo Kanalai",
    "expertise.performanceDesc": "Meta Ads, Google Ads, konversijomis grįsti piltuvai.",
    "expertise.retention": "Išlaikymas ir Lojalumas",
    "expertise.retentionDesc": "Naujienlaiškiai, pakartotinio nukreipimo kampanijos, automatizavimas.",
    "expertise.social": "Socialinės žiniasklaidos valdymas",
    "expertise.socialDesc": "Strategija ir turinio kalendorius, skelbimas, klientų aptarnavimas, analitika.",
    "expertise.website": "Svetainių Sprendimai",
    "expertise.websiteDesc": "Individualios svetainės, nukreipimo puslapiai, el. prekybos platformos.",
    "expertise.ai": "AI Automatizacija",
    "expertise.aiDesc": "AI skambučių agentai, AI video kūrimas, procesų automatizavimas.",
    
    // Process Section
    "process.title": "Mūsų",
    "process.process": "procesas",
    "process.subtitle": "Nuo vienos idėjos iki pilnos 360° augimo sistemos",
    "process.discovery": "Atradimas",
    "process.discoveryDesc": "Audituojame jūsų prekės ženklą, piltuvą ir duomenis reklamose, svetainėje, el. pašte ir socialiniuose tinkluose, kad atrastume tikras augimo galimybes.",
    "process.strategy": "Strategija",
    "process.strategyDesc": "Sukuriame vieną aiškų planą, kad kiekvienas kanalas dirbtų to paties tikslo link.",
    "process.execution": "Įgyvendinimas",
    "process.executionDesc": "Valdome Meta, Google, svetainę, el. paštą, socialinius tinklus ir video kaip vieną sujungtą sistemą.",
    "process.growth": "Augimas",
    "process.growthDesc": "Nuolatinis optimizavimas ir plėtra, pagrįsta duomenimis grįstomis įžvalgomis.",
    
    // Experience Section
    "experience.title": "Patirtis",
    "experience.subtitle": "Formuojame augimą įvairiose pramonės šakose.",
    "experience.wellness": "Formuojame",
    "experience.wellnessBrand": "gerovės",
    "experience.wellnessEnd": "prekės ženklus",
    "experience.ecommerce": "Formuojame",
    "experience.ecommerceGrowth": "el. prekybos",
    "experience.ecommerceEnd": "augimą",
    "experience.hospitality": "Formuojame",
    "experience.hospitalityExp": "svetingumo",
    "experience.hospitalityEnd": "patirtis",
    
    // Why Section
    "why.title": "Kodėl",
    "why.shape": "Shape",
    "why.subtitle": "Kodėl Shape, o ne Freelanceriai ar klasikinės agentūros?",
    "why.oneTeam": "Viena komanda. Jokios fragmentacijos.",
    "why.oneTeamDesc": "Daugiau nereikės žongliruoti 5 laisvais Freelanceriais — gausite vieną pilnai sinchronizuotą 360° komandą.",
    "why.endToEnd": "Kūryba + našumas po vienu stogu.",
    "why.endToEndDesc": "Mūsų medijos pirkėjai, dizaineriai ir video komanda dirba kaip vienas — greitesnis, aštresnis, efektyvesnis rezultatas.",
    "why.faster": "Aiški struktūra. Sklandus vykdymas.",
    "why.fasterDesc": "Vienas organizuotas procesas, nuosekli komunikacija ir jokio chaoso tarp kanalų.",
    "why.vision": "Jūsų vizija, paversta skaitmeniniu",
    "why.visionDesc": "SHAPE.",
    "why.experience": "Patirkite suvienytų rinkodaros metodų skirtumą",
    
    // Testimonials Section
    "testimonials.title": "Klientų",
    "testimonials.stories": "istorijos",
    "testimonials.sarah": "Shape visiškai transformavo mūsų skaitmeninę buvimą. Jų integruotas metodas davė rezultatus, viršijančius mūsų lūkesčius.",
    "testimonials.michael": "Darbas su Shape buvo sklandus. Viena komanda, viena vizija, išmatuojami rezultatai. Tiksliai to, ko mums reikėjo.",
    "testimonials.emma": "Holistinė strategija, kurią jie sukūrė, padėjo mums plėsti savo prekės ženklą visuose kanaluose. Tikrai įspūdingas darbas.",
    "testimonials.sarahRole": "Rinkodaros Direktorė",
    "testimonials.michaelRole": "Generalinis Direktorius",
    "testimonials.emmaRole": "Prekės Ženklo Vadovė",
    
    // CTA Section
    "cta.ready": "PASIRUOŠĘ",
    "cta.toTurn": "savo viziją paversti skaitmeniniu",
    "cta.shape": "SHAPE?",
    "cta.growthNow": "Formuokite savo augimą dabar",
    "cta.bookCall": "Rezervuoti skambutį",
    
    // Footer
    "footer.tagline": "Viziją paverčiame skaitmenine realybe.",
    "footer.quickLinks": "Greitos Nuorodos",
    "footer.capabilities": "Galimybės",
    "footer.experience": "Patirtis",
    "footer.solutions": "Sprendimai",
    "footer.whyUs": "Kodėl Mes",
    "footer.contact": "Kontaktai",
    "footer.stayUpdated": "Būkite Informuoti",
    "footer.getLatest": "Gaukite naujausias įžvalgas ir naujienas.",
    "footer.yourEmail": "Jūsų el. paštas",
    "footer.copyright": "© 2025 Shape. Visos teisės saugomos.",
    "footer.trustedBy": "Pasitiki inovatyvūs prekės ženklai visame pasaulyje",
    "footer.subscribeSuccess": "Sėkmė!",
    "footer.subscribeDesc": "Jūs užsiprenumeravote mūsų naujienlaiškį.",
    
    // Testimonial Names
    "testimonials.sarahName": "Sarah Johnson",
    "testimonials.michaelName": "Michael Chen",
    "testimonials.emmaName": "Emma Williams",
    "testimonials.sarahCompany": "TechCorp",
    "testimonials.michaelCompany": "GrowthLab",
    "testimonials.emmaCompany": "Wellness Co",
    
    // Auth
    "auth.welcome": "Sveiki Sugrįžę",
    "auth.join": "Prisijunkite prie SHAPE",
    "auth.signInDesc": "Prisijunkite, kad tęstumėte mokymąsi",
    "auth.signUpDesc": "Sukurkite paskyrą, kad pradėtumėte mokytis",
    "auth.fullName": "Pilnas Vardas",
    "auth.email": "El. paštas",
    "auth.password": "Slaptažodis",
    "auth.signIn": "Prisijungti",
    "auth.signUp": "Registruotis",
    "auth.noAccount": "Neturite paskyros? Registruokitės",
    "auth.haveAccount": "Jau turite paskyrą? Prisijunkite",
    "auth.backToMain": "Atgal į pradžią",
    
    // Common
    "common.loading": "Kraunama...",
    
    // Portfolio
    "portfolio.title": "Portfolio",
    "portfolio.subtitle": "Geriausiųjų kampanijų ir projektų įvairiais kanalais akcentai",
    "portfolio.all": "Visi Projektai",
    "portfolio.meta": "Meta Skelbimai",
    "portfolio.google": "Google Skelbimai",
    "portfolio.video": "Video Kūrimas",
    "portfolio.youtube": "YouTube",
    "portfolio.loading": "Kraunami projektai...",
    "portfolio.noProjects": "Šioje kategorijoje projektų nerasta.",
    "portfolio.photos": "nuotraukos",
    "portfolio.videos": "vaizdo įrašai",
    "portfolio.allClients": "Visi Klientai",
    "portfolio.client": "Klientas",
    "portfolio.instagramVideo": "Instagram vaizdo įrašas",
    "portfolio.openInstagram": "Atidaryti Instagram",
    
    // Navigation Menu
    "nav.process": "Procesas",
    "nav.workExamples": "Darbų Pavyzdžiai",
    "nav.portfolio": "Portfolio",
    "nav.contact": "Kontaktai",
    "nav.accountDashboard": "Paskyros Valdymas",
    "nav.adminPanel": "Administratoriaus Skydelis",
    "nav.myCourses": "Mano Kursai",
    
    // Account Dashboard
    "account.title": "Paskyros Valdymas",
    "account.subtitle": "Tvarkykite savo profilį ir nustatymus",
    "account.profileTab": "Profilio Valdymas",
    "account.learningTab": "Mano Mokymas",
    "account.settingsTab": "Paskyros Nustatymai",
    "account.profileTitle": "Profilio Informacija",
    "account.profileDesc": "Atnaujinkite savo profilio informaciją ir avatarą",
    "account.uploadAvatar": "Įkelti Avatarą",
    "account.uploading": "Keliama...",
    "account.avatarHint": "JPG, PNG arba GIF. Maks. dydis 2MB.",
    "account.email": "El. paštas",
    "account.emailHint": "El. pašto keisti čia negalima",
    "account.fullName": "Pilnas Vardas",
    "account.fullNamePlaceholder": "Įveskite savo pilną vardą",
    "account.bio": "Biografija",
    "account.bioPlaceholder": "Papasakokite apie save",
    "account.saveChanges": "Išsaugoti Pakeitimus",
    "account.myCourses": "Mano Kursai",
    "account.trackProgress": "Stebėkite savo mokymosi progresą",
    "account.noEnrollments": "Dar nesate užsiregistravę į jokius kursus",
    "account.browseCourses": "Naršyti Kursus",
    "account.progress": "Progresas",
    "account.continueLearning": "Tęsti Mokymąsi",
    "account.myCertificates": "Mano Sertifikatai",
    "account.achievements": "Jūsų uždirbti pasiekimai",
    "account.noCertificates": "Dar neužsidirbote sertifikatų",
    "account.earnCertificates": "Užbaikite kursus, kad gautumėte sertifikatus",
    "account.issuedOn": "Išduota",
    "account.view": "Peržiūrėti",
    "account.changePassword": "Keisti Slaptažodį",
    "account.updatePassword": "Atnaujinkite savo slaptažodį",
    "account.newPassword": "Naujas Slaptažodis",
    "account.confirmPassword": "Patvirtinti Slaptažodį",
    "account.passwordMinLength": "Slaptažodis turi būti bent 6 simbolių",
    "account.updatePasswordBtn": "Atnaujinti Slaptažodį",
    "account.deleteAccount": "Ištrinti Paskyrą",
    "account.deleteAccountDesc": "Visam laikui ištrinti jūsų paskyrą",
    "account.deleteWarning": "Šio veiksmo negalima atšaukti. Visi jūsų duomenys bus visam laikui ištrinti.",
    "account.deleteMyAccount": "Ištrinti Mano Paskyrą",
    
    // Admin Dashboard
    "admin.title": "Administratoriaus Skydelis",
    "admin.welcome": "Sveiki sugrįžę! Čia jūsų apžvalga",
    "admin.totalProjects": "Viso Projektų",
    "admin.portfolioItems": "Portfelio elementai",
    "admin.totalCourses": "Viso Kursų",
    "admin.publishedDrafts": "Publikuoti ir juodraščiai",
    "admin.totalStudents": "Viso Studentų",
    "admin.registeredUsers": "Registruoti vartotojai",
    "admin.enrollments": "Registracijos",
    "admin.activeCompleted": "Aktyvios ir užbaigtos",
    "admin.quickActions": "Greiti Veiksmai",
    "admin.portfolioMgmt": "Portfelio Valdymas",
    "admin.portfolioMgmtDesc": "Valdyti portfelio projektus ir mediją",
    "admin.courseMgmt": "Kursų Valdymas",
    "admin.courseMgmtDesc": "Valdyti kursus, modulius ir pamokas",
    "admin.userMgmt": "Vartotojų Valdymas",
    "admin.userMgmtDesc": "Valdyti vartotojus ir teises",
    "admin.settings": "Nustatymai",
    "admin.settingsDesc": "Konfigūruoti sistemos nustatymus",
    "admin.manage": "Valdyti",
    "admin.backToHome": "Atgal į Pradžią",
    "admin.backToAdmin": "Atgal į Admin",
    "admin.addCourse": "Pridėti Naują Kursą",
    "admin.editCourse": "Redaguoti Kursą",
    "admin.courseTitle": "Pavadinimas",
    "admin.courseSlug": "Slug",
    "admin.courseDescription": "Aprašymas",
    "admin.coursePrice": "Kaina",
    "admin.courseCurrency": "Valiuta",
    "admin.courseLevel": "Lygis",
    "admin.courseStatus": "Būsena",
    "admin.courseDuration": "Trukmė (valandos)",
    "admin.courseThumbnail": "Kurso Miniatiūra",
    "admin.uploadThumbnail": "Paspauskite įkelti miniatiūrą",
    "admin.thumbnailUploaded": "Miniatiūra sėkmingai įkelta",
    "admin.createCourse": "Sukurti Kursą",
    "admin.updateCourse": "Atnaujinti Kursą",
    "admin.existingCourses": "Esami Kursai",
    "admin.draft": "Juodraštis",
    "admin.published": "Publikuota",
    "admin.archived": "Archyvuota",
    "admin.addModule": "Pridėti Modulį",
    "admin.editModule": "Redaguoti Modulį",
    "admin.moduleTitle": "Modulio Pavadinimas",
    "admin.moduleDescription": "Modulio Aprašymas",
    "admin.createModule": "Sukurti Modulį",
    "admin.updateModule": "Atnaujinti Modulį",
    "admin.addLesson": "Pridėti Pamoką",
    "admin.editLesson": "Redaguoti Pamoką",
    "admin.lessonTitle": "Pamokos Pavadinimas",
    "admin.lessonDescription": "Pamokos Aprašymas",
    "admin.lessonType": "Tipas",
    "admin.lessonDuration": "Trukmė (min)",
    "admin.videoLesson": "Video",
    "admin.textLesson": "Tekstas",
    "admin.quizLesson": "Testas",
    "admin.videoUrl": "Video URL",
    "admin.lessonContent": "Turinys",
    "admin.freePreview": "Nemokama Peržiūra",
    "admin.createLesson": "Sukurti Pamoką",
    "admin.updateLesson": "Atnaujinti Pamoką",
    "admin.edit": "Redaguoti",
    "admin.delete": "Ištrinti",
    "admin.cancel": "Atšaukti",
    
    // Course Catalog
    "courses.title": "Mūsų Kursai",
    "courses.subtitle": "Mokykitės iš pramonės ekspertų ir tobulinkite savo karjerą",
    "courses.search": "Ieškoti kursų...",
    "courses.allLevels": "Visi Lygiai",
    "courses.beginner": "Pradedantis",
    "courses.intermediate": "Vidutinis",
    "courses.advanced": "Pažengęs",
    "courses.allPrices": "Visi",
    "courses.free": "Nemokama",
    "courses.paid": "Mokama",
    "courses.sortBy": "Rūšiuoti pagal",
    "courses.sortNewest": "Naujausias",
    "courses.sortPopular": "Populiariausias",
    "courses.sortPriceLow": "Kaina: Nuo žemiausios",
    "courses.sortPriceHigh": "Kaina: Nuo aukščiausios",
    "courses.featured": "Rekomenduojama",
    "courses.students": "studentai",
    "courses.hours": "valandos",
    "courses.viewCourse": "Peržiūrėti Kursą",
    "courses.noCourses": "Nerasta kursų pagal jūsų kriterijus",
    "courses.loading": "Kraunami kursai...",
    "courses.instructor": "Instruktorius",
    "courses.workingOnIt": "Šis puslapis dar kuriamas",
    
    // Course Detail
    "courseDetail.overview": "Apžvalga",
    "courseDetail.curriculum": "Programa",
    "courseDetail.reviews": "Atsiliepimai",
    "courseDetail.instructor": "Instruktorius",
    "courseDetail.enrollNow": "Registruotis Dabar",
    "courseDetail.enrolled": "Užsiregistruota",
    "courseDetail.studentsEnrolled": "studentų užsiregistravo",
    "courseDetail.whatYouLearn": "Ką išmoksite",
    "courseDetail.requirements": "Reikalavimai",
    "courseDetail.description": "Aprašymas",
    "courseDetail.modules": "moduliai",
    "courseDetail.lessons": "pamokos",
    "courseDetail.totalDuration": "Bendra trukmė",
    "courseDetail.level": "Lygis",
    "courseDetail.lastUpdated": "Paskutinį kartą atnaujinta",
    "courseDetail.language": "Kalba",
    "courseDetail.certificate": "Baigimo sertifikatas",
    "courseDetail.preview": "Peržiūra",
    "courseDetail.locked": "Užrakinta",
    "courseDetail.minutes": "min",
    "courseDetail.noReviews": "Dar nėra atsiliepimų. Būkite pirmas!",
    "courseDetail.writeReview": "Parašyti Atsiliepimą",
    "courseDetail.yourRating": "Jūsų Įvertinimas",
    "courseDetail.yourReview": "Jūsų Atsiliepimas",
    "courseDetail.submit": "Pateikti Atsiliepimą",
    "courseDetail.instructorBio": "Apie Instruktorių",
    "courseDetail.otherCourses": "Kiti šio instruktoriaus kursai",
    "courseDetail.enrollmentSuccess": "Sėkmingai užsiregistravote į kursą!",
    "courseDetail.enrollmentError": "Nepavyko užsiregistruoti. Bandykite dar kartą.",
    "courseDetail.reviewSuccess": "Atsiliepimas pateiktas sėkmingai!",
    "courseDetail.reviewError": "Nepavyko pateikti atsiliepimo. Bandykite dar kartą.",
    "courseDetail.loginToEnroll": "Prisijunkite, kad užsiregistruotumėte",
    "courseDetail.loginToReview": "Prisijunkite, kad parašytumėte atsiliepimą",
    "courseDetail.alreadyEnrolled": "Jūs užsiregistravote į šį kursą",
    "courseDetail.continueLearning": "Tęsti Mokymąsi",
    "courseDetail.reviewPlaceholder": "Pasidalinkite savo mintimis apie šį kursą...",
    "courseDetail.submitReview": "Pateikti atsiliepimą",
    "courseDetail.startLearning": "Pradėti mokymąsi",
    
    // Course Player
    "coursePlayer.courseContent": "Kurso turinys",
    "coursePlayer.completed": "Baigta",
    "coursePlayer.markComplete": "Pažymėti kaip baigtą",
    "coursePlayer.nextLesson": "Kita pamoka",
    "coursePlayer.previousLesson": "Ankstesnė pamoka",
    "coursePlayer.backToCourse": "Grįžti į kursą",
    "coursePlayer.progress": "Pažanga",
    "coursePlayer.lesson": "Pamoka",
    "coursePlayer.of": "iš",
    "coursePlayer.noContent": "Šiai pamokai nėra turinio.",
    "coursePlayer.loadingLesson": "Įkeliama pamoka...",
    "coursePlayer.completedLesson": "Pamoka baigta!",
    "coursePlayer.errorLoading": "Klaida įkeliant pamoką. Bandykite dar kartą.",
    "coursePlayer.notEnrolled": "Turite būti užsiregistravę, kad galėtumėte pasiekti šį kursą.",
    "coursePlayer.enrollNow": "Registruotis dabar",
    
    // Course Editor
    "courseEditor.editMode": "Redagavimo režimas",
    "courseEditor.editLesson": "Redaguoti pamoką",
    "courseEditor.lessonTitle": "Pamokos pavadinimas",
    "courseEditor.lessonTitlePlaceholder": "Įveskite pamokos pavadinimą...",
    "courseEditor.lessonType": "Pamokos tipas",
    "courseEditor.typeVideo": "Vaizdo įrašas",
    "courseEditor.typeText": "Tekstas",
    "courseEditor.typeQuiz": "Testas",
    "courseEditor.videoUrl": "Vaizdo įrašo URL",
    "courseEditor.videoUrlHint": "Naudokite YouTube embed URL (pvz., https://www.youtube.com/embed/VIDEO_ID)",
    "courseEditor.description": "Aprašymas",
    "courseEditor.descriptionPlaceholder": "Trumpas pamokos aprašymas...",
    "courseEditor.content": "Turinys (HTML)",
    "courseEditor.contentPlaceholder": "Įveskite pamokos turinį su HTML formatavimu...",
    "courseEditor.contentHint": "Galite naudoti HTML žymes formatavimui (pvz., <h2>, <p>, <ul>, <img>)",
    "courseEditor.duration": "Trukmė (minutės)",
    "courseEditor.freePreview": "Nemokama peržiūra",
    "courseEditor.yes": "Taip",
    "courseEditor.no": "Ne",
    "courseEditor.save": "Išsaugoti",
    "courseEditor.cancel": "Atšaukti",
    "courseEditor.lessonSaved": "Pamoka sėkmingai išsaugota!",
    "courseEditor.saveFailed": "Nepavyko išsaugoti pamokos. Bandykite dar kartą.",
    "courseEditor.editingAs": "Redaguojate kaip",
    "courseEditor.clickToEdit": "Spauskite redaguoti",
    
    // Rich Text Editor
    "richEditor.heading": "Antraštė",
    "richEditor.paragraph": "Pastraipa",
    "richEditor.bold": "Pusjuodis",
    "richEditor.italic": "Kursyvas",
    "richEditor.underline": "Pabrauktas",
    "richEditor.strikethrough": "Perbrauktas",
    "richEditor.bulletList": "Sąrašas",
    "richEditor.numberedList": "Numeruotas sąrašas",
    "richEditor.alignLeft": "Lygiuoti kairėn",
    "richEditor.alignCenter": "Lygiuoti per vidurį",
    "richEditor.alignRight": "Lygiuoti dešinėn",
    "richEditor.insertLink": "Įterpti nuorodą",
    "richEditor.linkUrl": "Nuorodos URL",
    "richEditor.insertImage": "Įterpti nuotrauką",
    "richEditor.imageUrl": "Nuotraukos URL",
    "richEditor.uploadImage": "Įkelti nuotrauką",
    "richEditor.insertVideo": "Įterpti vaizdo įrašą",
    "richEditor.videoUrl": "Vaizdo įrašo URL",
    "richEditor.videoHint": "Palaiko YouTube ir Vimeo nuorodas",
    "richEditor.codeBlock": "Kodo blokas",
    "richEditor.blockquote": "Citata",
    "richEditor.horizontalRule": "Horizontali linija",
    "richEditor.undo": "Atšaukti",
    "richEditor.redo": "Perdaryti",
    "richEditor.insert": "Įterpti",
    "richEditor.or": "arba",
    "richEditor.preview": "Peržiūra",
    "richEditor.edit": "Redaguoti",
    "richEditor.invalidImageType": "Prašome pasirinkti nuotraukos failą",
    "richEditor.imageTooLarge": "Nuotrauka turi būti mažesnė nei 5MB",
    "richEditor.imageUploaded": "Nuotrauka sėkmingai įkelta",
    "richEditor.uploadFailed": "Nepavyko įkelti nuotraukos",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("en");

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  return context;
};
